import { supabase } from './supabaseClient';
import React, { useState, useEffect, useRef } from "react";
import { 
  Plus, Calendar, User, Package, FileText, AlertCircle, Wrench, 
  Download, Upload, Shield, Cloud, CloudOff, Wifi, WifiOff, 
  Lock, Eye, EyeOff, ChevronDown, LogOut, Users, ShoppingCart, 
  Database, Menu, Search, Settings, Trash2
} from "lucide-react";
import { ServiceWorkerManager } from './components/ServiceWorkerManager';
import NyRegistrering from "./components/NyRegistrering";
import TilBestilling from "./components/TilBestilling";
import Bestilt from "./components/Bestilt";
import Service from "./components/Service";
import KlarTilHenting from "./components/KlarTilHenting";
import KlarTilFakturering from "./components/KlarTilFakturering";
import DiverseVent from "./components/DiverseVent";
import Reklamasjon from "./components/Reklamasjon";
import Arkiv from "./components/Arkiv";
import Kunder from "./components/Kunder";
import Produkter from "./components/Produkter";
import FeedbackBox from './components/FeedbackBox';
import MaintenanceDialog from './components/maintenance/MaintenanceDialog';
import MaintenancePage from './components/maintenance/MaintenancePage';
import MaintenanceWarning from './components/maintenance/MaintenanceWarning';
import InactivityWarningDialog from './components/InactivityWarningDialog';
import UserManagement from './components/UserManagement';
import { AlertTriangle } from 'lucide-react';
import { useMaintenanceManager } from './hooks/useMaintenanceManager';
import { useSessionManager } from './hooks/useSessionManager';
import { Case, Customer, Category } from "./types/types";
import { supabaseService } from "./SupabaseService";
import { ToastContainer, useNotifications, MoveBackwardsDialog } from './components/NotificationSystem';
import Papirkurv from "./components/Papirkurv";
import AppSwitcher, { AppMode } from './components/shared/AppSwitcher';
import StrandApp from './components/strand/StrandApp';

const PASSWORDS = [
  "Sthag2025!", "KuliSTback93!kan?", "strand456DuiHvm6fls?EdcB"
];

const USER_PINS: { [key: string]: string } = {
  "8947": "Johan", "3549": "Tormod", "4776": "Rose", "3007": "Odd Arthur", "1349": "Trond Øyvind", "3646": "Robert",
};

const LoginScreen: React.FC<{ onLogin: () => void }> = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPinInput, setShowPinInput] = useState(false);
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [rememberDevice, setRememberDevice] = useState(true);

  useEffect(() => {
    const deviceTrusted = localStorage.getItem("hagia-device-trusted");
    if (deviceTrusted === "true") setShowPinInput(true);
  }, []);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (PASSWORDS.includes(password)) {
      if (rememberDevice) localStorage.setItem("hagia-device-trusted", "true");
      setShowPinInput(true);
      setError("");
    } else {
      setError("Feil passord. Prøv igjen.");
      setAttempts(attempts + 1);
      setPassword("");
      if (attempts >= 2) setError("Feil passord. Hint: Kontakt Tormod for passord.");
    }
  };

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (USER_PINS[pin]) {
      localStorage.setItem("hagia-user-name", USER_PINS[pin]);
      localStorage.setItem("hagia-user-pin", pin);
      onLogin();
    } else {
      setError("Ugyldig PIN-kode. Prøv igjen.");
      setPin("");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="text-green-600" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Hagia Strand</h1>
          <p className="text-gray-600">Maskinutleie AS • Servicesystem</p>
        </div>

        {!showPinInput ? (
          localStorage.getItem("hagia-device-trusted") !== "true" ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Passord</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {setPassword(e.target.value); setError("");}}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-12"
                    placeholder="Skriv inn passord"
                    autoFocus
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="rememberDevice" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
                <label htmlFor="rememberDevice" className="ml-2 block text-sm text-gray-700">Husk denne enheten (hopp over passord neste gang)</label>
              </div>
              {error && (<div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">{error}</div>)}
              <button type="submit" className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 font-medium">Fortsett</button>
            </form>
          ) : null
        ) : (
          <form onSubmit={handlePinSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PIN-kode</label>
              <input type="text" value={pin} onChange={(e) => {
  const newPin = e.target.value;
  setPin(newPin);
  setError("");
  
  // Automatisk innlogging når 4 siffer er tastet
  if (newPin.length === 4) {
    if (USER_PINS[newPin]) {
      localStorage.setItem("hagia-user-name", USER_PINS[newPin]);
      localStorage.setItem("hagia-user-pin", newPin);
      onLogin();
    } else {
      setError("Ugyldig PIN-kode. Prøv igjen.");
      setPin("");
    }
  }
}} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-center text-2xl font-bold tracking-wider" placeholder="• • • •" maxLength={4} autoFocus required />
            </div>
            {error && (<div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">{error}</div>)}
            <button type="submit" className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 font-medium">Logg inn</button>
          </form>
        )}
      </div>
    </div>
  );
};

const DataSikring: React.FC<{
  cases: Case[]; setCases: React.Dispatch<React.SetStateAction<Case[]>>; customers: Customer[]; setCustomers: React.Dispatch<React.SetStateAction<Customer[]>>; onLogout: () => void; showSuccess?: (message: string, title?: string) => void; showError?: (message: string, title?: string) => void; showInfo?: (message: string, title?: string) => void;
}> = ({ cases, setCases, customers, setCustomers, onLogout, showSuccess, showError, showInfo }) => {
  
  const handleExportData = async () => {
    const data = { cases, customers, exportDate: new Date().toISOString(), version: "2.0" };
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `hagia-backup-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    await supabaseService.logSystemEvent('EXPORT', {
      user: localStorage.getItem('hagia-user-name'), customers_count: customers.length, cases_count: cases.length, file_name: link.download, timestamp: new Date().toISOString()
    });
    
    if (showSuccess) showSuccess(`Eksportert ${cases.length} ordre og ${customers.length} kunder`, "Eksport fullført");
  };

  const handleImportData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content);

        if (!data.cases || !data.customers) {
          if (showError) showError("Ugyldig backup-fil. Mangler data.", "Import feil");
          return;
        }

        const confirmImport = window.confirm(`📥 IMPORT BEKREFTELSE\n\nFilen inneholder:\n• ${data.customers?.length || 0} kunder\n• ${data.cases?.length || 0} ordre\n\nFortsette med import?`);
        if (!confirmImport) return;

        let successfulCustomers = 0, failedCustomers = 0, successfulCases = 0, failedCases = 0;

        for (const customer of data.customers) {
          try {
            if (!customer.id || !customer.name) { failedCustomers++; continue; }
            if (typeof customer.id === 'string') customer.id = parseInt(customer.id);
            if (isNaN(customer.id)) { failedCustomers++; continue; }
            
            const saved = await supabaseService.saveCustomer(customer);
            if (saved) successfulCustomers++; else failedCustomers++;
            if (successfulCustomers % 100 === 0) console.log(`Progress: ${successfulCustomers}/${data.customers.length} kunder importert`);
          } catch (error) {
            console.error(`Feil ved import av kunde ${customer.id}:`, error);
            failedCustomers++;
          }
        }

        for (const caseItem of data.cases) {
          try {
            if (!caseItem.id) { failedCases++; continue; }
            if (typeof caseItem.id === 'string') caseItem.id = parseInt(caseItem.id);
            if (isNaN(caseItem.id)) { failedCases++; continue; }
            if (typeof caseItem.varelinjer === 'string') {
              try { caseItem.varelinjer = JSON.parse(caseItem.varelinjer); } catch { caseItem.varelinjer = []; }
            }
            
            const saved = await supabaseService.saveCase(caseItem);
            if (saved) successfulCases++; else failedCases++;
          } catch (error) {
            console.error(`Feil ved import av ordre ${caseItem.id}:`, error);
            failedCases++;
          }
        }

        const [loadedCases, loadedCustomers] = await Promise.all([supabaseService.loadCases(), supabaseService.loadCustomers()]);
        setCases(loadedCases);
        setCustomers(loadedCustomers);

        await supabaseService.logSystemEvent('IMPORT', {
          user: localStorage.getItem('hagia-user-name'), customers_imported: successfulCustomers, customers_failed: failedCustomers, cases_imported: successfulCases, cases_failed: failedCases, total_customers_after: loadedCustomers.length, total_cases_after: loadedCases.length, file_name: file.name, file_size: file.size, timestamp: new Date().toISOString()
        });
        
        if (showSuccess) showSuccess(`Importert ${successfulCustomers} kunder og ${successfulCases} ordre`, "Import fullført");
        if (failedCustomers > 0 || failedCases > 0) {
          if (showError) showError(`${failedCustomers} kunder og ${failedCases} ordre feilet`, "Import advarsler");
        }
      } catch (error: any) {
        await supabaseService.logSystemEvent('IMPORT_ERROR', {
          user: localStorage.getItem('hagia-user-name'), error: error.message, file_name: file.name, timestamp: new Date().toISOString()
        });
        console.error("Import error:", error);
        if (showError) showError("Feil ved import. Se konsollen for detaljer.", "Import feil");
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <Shield className="text-blue-600" size={32} />
          <h2 className="text-2xl font-bold text-gray-900">Datasikring</h2>
        </div>
        <p className="text-gray-600">Sikre og gjenopprett systemdata</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
          <div className="text-center mb-4">
            <Download className="text-purple-600 mx-auto mb-3" size={40} />
            <h3 className="text-lg font-semibold text-purple-800 mb-2">Eksporter til fil</h3>
            <p className="text-purple-700 text-sm">Last ned backup som JSON-fil</p>
          </div>
          <button onClick={handleExportData} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium transition-colors">Eksporter</button>
        </div>
        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6">
          <div className="text-center mb-4">
            <Upload className="text-orange-600 mx-auto mb-3" size={40} />
            <h3 className="text-lg font-semibold text-orange-800 mb-2">Importer fra fil</h3>
            <p className="text-orange-700 text-sm">Gjenopprett fra JSON backup-fil</p>
          </div>
          <label className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-medium transition-colors cursor-pointer block text-center">
            Velg fil <input type="file" accept=".json" onChange={handleImportData} className="hidden" />
          </label>
        </div>
      </div>
      <div className="mt-6 flex justify-center">
        <button onClick={onLogout} className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors">
          <Lock size={16} /><span>Logg ut</span>
        </button>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [currentApp, setCurrentApp] = useState<AppMode>('hagia');
  const [activeTab, setActiveTab] = useState<string>("ny-registrering");
  const [editingCase, setEditingCase] = useState<Case | null>(null);
  const [originalCategory, setOriginalCategory] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [cases, setCases] = useState<Case[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [archivedCases, setArchivedCases] = useState<Case[]>([]);
  const [deletedCases, setDeletedCases] = useState<Case[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [userRole, setUserRole] = useState<any>(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [canAccessMaintenance, setCanAccessMaintenance] = useState(false);

  // SØKEFUNKSJON STATE
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Case[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [selectedArchivedCaseId, setSelectedArchivedCaseId] = useState<number | null>(null);
  
  const { notifications, showSuccess, showError, showWarning, showInfo, removeNotification } = useNotifications();
  const maintenanceManager = useMaintenanceManager(showInfo, showWarning, showError);
  const sessionManager = useSessionManager(
  isAuthenticated,
  () => {
    localStorage.removeItem("hagia-user-name");
    localStorage.removeItem("hagia-user-pin");
    localStorage.removeItem("hagia-login-time");
    setIsAuthenticated(false);
    setActiveTab("ny-registrering");
    setEditingCase(null);
    setOriginalCategory(null);
    setHasUnsavedChanges(false);
  },
  showInfo,
  showWarning
);
  const [showMaintenanceWarning, setShowMaintenanceWarning] = useState(false);
  const [maintenanceWarningData, setMaintenanceWarningData] = useState<{
  message: string;
  minutesUntil: number;
  } | null>(null);
  const handleMaintenanceStart = async (message: string, estimatedMinutes: number) => {
  return await maintenanceManager.startMaintenance(message, estimatedMinutes);
};

const handleMaintenanceEnd = async () => {
  return await maintenanceManager.endMaintenance();
};
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [pendingApp, setPendingApp] = useState<AppMode | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showMoveBackwardsDialog, setShowMoveBackwardsDialog] = useState(false);
  const [moveBackwardsData, setMoveBackwardsData] = useState<{
  fromCategory: string;
  toCategory: string;
  caseName: string;
  resolve: (value: boolean) => void;
} | null>(null);

  // SØKEFUNKSJON
  const handleSearch = (term: string) => {
  setSearchTerm(term);
  
  if (!term.trim()) {
    setSearchResults([]);
    setShowSearchResults(false);
    return;
  }

  const searchTermLower = term.toLowerCase();
  const allCases = [...cases, ...archivedCases];
   const filteredCases = allCases.filter(case_ => {
    const searchFields = [
      case_.kunde,
      case_.maskinMerke,
      case_.beskrivelse,
      case_.tlf,
      case_.serienr,
      case_.id.toString(),
      case_.orgnr,
      case_.kommentar
    ];
    
    return searchFields.some(field => 
      field && field.toString().toLowerCase().includes(searchTermLower)
    );
  });

  // FORBEDRET SORTERING: Arkiv alltid til bunnen
    const sortedResults = filteredCases.sort((a, b) => {
      // Arkiverte ordre alltid til bunnen
      if (a.kategori === "Arkiv" && b.kategori !== "Arkiv") return 1;
      if (b.kategori === "Arkiv" && a.kategori !== "Arkiv") return -1;
      
      // Blant ikke-arkiverte: sorter etter relevans og dato
      if (a.kategori !== "Arkiv" && b.kategori !== "Arkiv") {
        // Service og "Klar til henting" først (mest aktuelle)
        const highPriorityA = ["Service", "Klar til henting"].includes(a.kategori);
        const highPriorityB = ["Service", "Klar til henting"].includes(b.kategori);
        
        if (highPriorityA && !highPriorityB) return -1;
        if (highPriorityB && !highPriorityA) return 1;
        
        // Deretter sorter etter opprettelsesdato (nyeste først)
        const dateA = new Date(a.opprettet || a.dato).getTime();
        const dateB = new Date(b.opprettet || b.dato).getTime();
        return dateB - dateA;
      }
      
      // Blant arkiverte: sorter etter dato (nyeste først)
      const dateA = new Date(a.opprettet || a.dato).getTime();
      const dateB = new Date(b.opprettet || b.dato).getTime();
      return dateB - dateA;
    });

    setSearchResults(sortedResults.slice(0, 50)); // Begrens til 50 resultater
    setShowSearchResults(true);
  };

  useEffect(() => {
  if (!isAuthenticated) return;

  // 1. Sjekk om det finnes et aktivt varsel når komponenten mountes
  const checkExistingWarning = async () => {
    const warning = await supabaseService.getActiveMaintenanceWarning();
    if (warning) {
      const minutesLeft = Math.max(0, 
        Math.floor((new Date(warning.expires_at).getTime() - Date.now()) / 60000)
      );
      
      if (minutesLeft > 0) {
        setMaintenanceWarningData({
          message: warning.message,
          minutesUntil: minutesLeft
        });
        setShowMaintenanceWarning(true);
      }
    }
  };

  checkExistingWarning();

   const eventsChannel = supabase
    .channel('maintenance-events')
    .on(
      'postgres_changes',
      { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'system_events',
        filter: 'event_type=eq.MAINTENANCE_WARNING'
      },
      (payload) => {
        const eventData = payload.new.event_data;
        setMaintenanceWarningData({
          message: eventData.message || 'Systemvedlikehold starter snart',
          minutesUntil: eventData.minutes_until || 5
        });
        setShowMaintenanceWarning(true);
      }
    )
    .subscribe();

  // 3. Lytt til broadcast-meldinger for umiddelbar varsling
  const broadcastChannel = supabase
    .channel('maintenance-broadcast')
    .on(
      'broadcast',
      { event: 'maintenance-warning' },
      (payload) => {
        setMaintenanceWarningData({
          message: payload.payload.message || 'Systemvedlikehold starter snart',
          minutesUntil: payload.payload.minutesUntil || 5
        });
        setShowMaintenanceWarning(true);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(eventsChannel);
    supabase.removeChannel(broadcastChannel);
  };

}, [isAuthenticated]);

  const handleSearchResultClick = (case_: Case) => {
  // Sjekk om ordren er arkivert - gå til arkiv-fanen og vis detaljer
  if (case_.kategori === "Arkiv") {
    setSelectedArchivedCaseId(case_.id);
    setActiveTab("Arkiv");
    setSearchTerm("");
    setShowSearchResults(false);
    return;
  }
  
  openCaseForEditing(case_);
  setSearchTerm("");
  setShowSearchResults(false);
};

useEffect(() => {
  const checkUserRole = async () => {
    const userName = localStorage.getItem('hagia-user-name');
    if (userName && isAuthenticated) {
      const role = await supabaseService.getUserRole(userName);
      setUserRole(role);
      
      // Sjekk vedlikeholdstilgang
      const hasAccess = await supabaseService.canAccessMaintenance(userName);
      setCanAccessMaintenance(hasAccess);
    }
  };
  
  checkUserRole();
}, [isAuthenticated]);

  // 1. Sjekk om det finnes et aktivt varsel når komponenten mountes
  const checkExistingWarning = async () => {
    const warning = await supabaseService.getActiveMaintenanceWarning();
    if (warning) {
      const minutesLeft = Math.max(0, 
        Math.floor((new Date(warning.expires_at).getTime() - Date.now()) / 60000)
      );
      
      if (minutesLeft > 0) {
        setMaintenanceWarningData({
          message: warning.message,
          minutesUntil: minutesLeft
        });
        setShowMaintenanceWarning(true);
      }
    }
  };


  
  checkExistingWarning();
  useEffect(() => {
    const checkAuth = () => {
      const userPin = localStorage.getItem("hagia-user-pin");
      const userName = localStorage.getItem("hagia-user-name");
      if (userPin && userName) setIsAuthenticated(true);
      setIsCheckingAuth(false);
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) setShowSearchResults(false);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); showSuccess("Tilkoblet internett"); };
    const handleOffline = () => { setIsOnline(false); showWarning("Mistet internettforbindelse - endringer lagres lokalt"); };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => { window.removeEventListener("online", handleOnline); window.removeEventListener("offline", handleOffline); };
  }, [showSuccess, showWarning]);

  useEffect(() => {
  if (!isAuthenticated) return;
  (window as any).showMoveBackwardsConfirm = showMoveBackwardsConfirm;
  
  const loadInitialData = async () => {
  setIsLoading(true);
  try {
    const [loadedCases, archivedCases, loadedCustomers] = await Promise.all([
      supabaseService.loadCases(), 
      supabaseService.loadArchivedCases(),
      supabaseService.loadCustomers()
    ]);
    setCases(loadedCases);
    setArchivedCases(archivedCases);
    setCustomers(loadedCustomers);
    
    // Last deletedCases separat (feiler ikke hele loadingen hvis dette feiler)
    try {
      const deletedCases = await supabaseService.loadDeletedCases();
      setDeletedCases(deletedCases);
    } catch (error) {
      console.log('Kunne ikke laste papirkurv:', error);
      setDeletedCases([]); // Sett tom array hvis det feiler
    }
    
    setLastSync(new Date());
    showSuccess("Data lastet inn");
  } catch (error) {
    console.error("Error loading initial data:", error);
    showError("Kunne ikke laste data", "Feil");
    const savedCases = localStorage.getItem("hagia-cases");
    const savedCustomers = localStorage.getItem("hagia-customers");
    if (savedCases) setCases(JSON.parse(savedCases));
    if (savedCustomers) setCustomers(JSON.parse(savedCustomers));
  }
  setIsLoading(false);
};
  loadInitialData();
}, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !isOnline) return;
    const handleCaseChange = (type: string, newRecord: any, oldRecord: any) => {
      switch (type) {
        case 'INSERT': setCases(prev => { if (!prev.find(c => c.id === newRecord.id)) { showInfo(`Ny ordre #${newRecord.id} lagt til`); return [...prev, newRecord]; } return prev; }); break;
        case 'UPDATE': setCases(prev => prev.map(c => c.id === newRecord.id ? newRecord : c)); break;
        case 'DELETE': setCases(prev => prev.filter(c => c.id !== oldRecord.id)); break;
      }
    };
    const handleCustomerChange = (type: string, newRecord: any, oldRecord: any) => {
      switch (type) {
        case 'INSERT': setCustomers(prev => [...prev, newRecord]); break;
        case 'UPDATE': setCustomers(prev => prev.map(c => c.id === newRecord.id ? newRecord : c)); break;
        case 'DELETE': setCustomers(prev => prev.filter(c => c.id !== oldRecord.id)); break;
      }
    };
    supabaseService.startRealtimeSync(handleCaseChange, handleCustomerChange);
    setRealtimeConnected(true);
    return () => { supabaseService.stopRealtimeSync(); setRealtimeConnected(false); };
  }, [isAuthenticated, isOnline]);

  const handleLogin = async () => {
  const loginTime = new Date().toISOString();
  localStorage.setItem('hagia-login-time', loginTime);
  
  // VIKTIG: Nullstill siste aktivitet ved ny innlogging
  localStorage.setItem('hagia-last-activity', loginTime);
  
  // Nullstill inaktivitetsvarsel i session manager
  sessionManager.resetInactivityWarning();
  
  setIsAuthenticated(true);
  
  await supabaseService.logSystemEvent('LOGIN', {
    user: localStorage.getItem('hagia-user-name'), 
    pin: localStorage.getItem('hagia-user-pin'), 
    login_time: loginTime, 
    timestamp: loginTime
  });
  
  showSuccess(`Velkommen, ${localStorage.getItem('hagia-user-name')}!`);
};

  const handleLogout = async () => {
  try {
    await supabaseService.logSystemEvent('MANUAL_LOGOUT', {
      user: localStorage.getItem('hagia-user-name'),
      login_time: localStorage.getItem('hagia-login-time'),
      logout_time: new Date().toISOString(),
      reason: 'Manuell utlogging'
    });
    showInfo("Logget ut");
  } catch (error) {
    console.error('Feil ved utlogging:', error);
    showInfo("Logget ut");
  }
  
  // Session manager håndterer cleanup
  sessionManager.clearAllEditingTimeouts();
  
  // LEGG TIL DENNE LINJEN:
  sessionManager.resetInactivityWarning();
  
  // Nullstill siste aktivitet
  localStorage.removeItem('hagia-last-activity');
  
  setIsAuthenticated(false);
  setActiveTab("ny-registrering");
  setEditingCase(null);
  setOriginalCategory(null);
  setHasUnsavedChanges(false);
};

const handleTabChange = (newTab: string) => {
  // Hvis vi redigerer en ordre og trykker på ny-registrering igjen
  if (editingCase && newTab === "ny-registrering" && activeTab === "ny-registrering") {
    if (hasUnsavedChanges) {
      // Vis advarsel om ulagrede endringer
      setPendingTab("exit-editing");
      setShowUnsavedDialog(true);
      return;
    } else {
      // Ingen ulagrede endringer, gå ut av redigeringsmodus
      clearEditingCase();
      return;
    }
  }

  // Sjekk for ulagrede endringer ved tab-bytte (både for ny registrering og redigering)
  if (hasUnsavedChanges && newTab !== activeTab) {
    setPendingTab(newTab);
    setShowUnsavedDialog(true);
    return;
  }

  if (editingCase && newTab !== "ny-registrering") {
    clearEditingCase();
  }

  setActiveTab(newTab);
  setHasUnsavedChanges(false);
};

const handleAppSwitch = (newApp: AppMode) => {
  // Sjekk for ulagrede endringer før bytte av app (både for ny registrering og redigering)
  if (hasUnsavedChanges && newApp !== currentApp) {
    setPendingApp(newApp);
    setShowUnsavedDialog(true);
    return;
  }

  // Hvis ingen ulagrede endringer, bytt app direkte
  if (editingCase) {
    clearEditingCase();
  }
  setCurrentApp(newApp);
  setHasUnsavedChanges(false);
};

// Oppdater også dialog-håndteringen for å støtte "exit-editing":
  const openCaseForEditing = (case_: Case) => {
  setOriginalCategory(case_.kategori);
  setEditingCase({...case_});
  handleTabChange("ny-registrering");

   // Scroll til toppen av siden når redigeringsmodus starter
  setTimeout(() => {
    window.scrollTo({ 
      top: 0, 
      behavior: 'smooth' 
    });
  }, 100);
  
  // Bruk session manager i stedet for manual timeout
  sessionManager.startEditingTimeout(case_.id, (caseId) => {
    // Callback som kjøres ved timeout
    clearEditingCase();
  });
};

  const clearEditingCase = () => {
  if (editingCase) {
    sessionManager.clearEditingTimeout(editingCase.id);
  }
  setEditingCase(null);
  setOriginalCategory(null);
  setHasUnsavedChanges(false);
};

  const showMoveBackwardsConfirm = (fromCategory: string, toCategory: string, caseName: string): Promise<boolean> => {
  return new Promise((resolve) => {
    setMoveBackwardsData({
      fromCategory,
      toCategory, 
      caseName,
      resolve
    });
    setShowMoveBackwardsDialog(true);
  });
};

const handleMoveBackwardsConfirm = () => {
  if (moveBackwardsData) {
    moveBackwardsData.resolve(true);
    setShowMoveBackwardsDialog(false);
    setMoveBackwardsData(null);
  }
};

const handleMoveBackwardsCancel = () => {
  if (moveBackwardsData) {
    moveBackwardsData.resolve(false);
    setShowMoveBackwardsDialog(false);
    setMoveBackwardsData(null);
  }
};

  const categories: Category[] = [
    { id: "ny-registrering", name: "Ny registrering", icon: Plus, color: "bg-green-500" },
    { id: "Til bestilling", name: "Til bestilling", icon: Calendar, color: "bg-yellow-500" },
    { id: "Bestilt", name: "Bestilt", icon: Package, color: "bg-blue-500" },
    { id: "Service", name: "Service", icon: Wrench, color: "bg-green-500" },
    { id: "Klar til henting", name: "Klar til henting", icon: Package, color: "bg-orange-500" },
    { id: "Klar til fakturering", name: "Klar til fakturering", icon: FileText, color: "bg-purple-500" },
    { id: "Reklamasjon", name: "Reklamasjon", icon: AlertCircle, color: "bg-red-500" },
    { id: "Diverse/Vent", name: "Diverse/Vent", icon: Calendar, color: "bg-gray-500" },
    { id: "Arkiv", name: "Arkiv", icon: FileText, color: "bg-gray-400" },
    { id: "Papirkurv", name: "Papirkurv", icon: Trash2, color: "bg-red-400" },
  ];

  const renderActiveComponent = () => {
    const commonProps = { cases, setCases, customers, setCustomers, openCaseForEditing, showSuccess, showError, showWarning, showInfo };
    switch (activeTab) {
      case "ny-registrering": return (<NyRegistrering key={editingCase?.id || 'new'} {...commonProps} editingCase={editingCase} clearEditingCase={clearEditingCase} hasUnsavedChanges={hasUnsavedChanges} setHasUnsavedChanges={setHasUnsavedChanges} originalCategory={originalCategory} deletedCases={deletedCases} setDeletedCases={setDeletedCases} />);
      case "Til bestilling": return <TilBestilling {...commonProps} />;
      case "Bestilt": return <Bestilt {...commonProps} />;
      case "Service": return <Service {...commonProps} />;
      case "Klar til henting": return <KlarTilHenting {...commonProps} />;
      case "Klar til fakturering": return <KlarTilFakturering {...commonProps} />;
      case "Diverse/Vent": return <DiverseVent {...commonProps} />;
      case "Reklamasjon": return <Reklamasjon {...commonProps} />;
      case "Arkiv": return <Arkiv {...commonProps} archivedCases={archivedCases} setArchivedCases={setArchivedCases} selectedCaseId={selectedArchivedCaseId} onCaseViewed={() => setSelectedArchivedCaseId(null)}/>;
      case "kunder": return <Kunder {...commonProps} />;
      case "produkter": return <Produkter {...commonProps} />;
      case "datasikring": return (<DataSikring cases={cases} setCases={setCases} customers={customers} setCustomers={setCustomers} onLogout={handleLogout} showSuccess={showSuccess} showError={showError} showInfo={showInfo} />);
      case "Papirkurv": return <Papirkurv {...commonProps} deletedCases={deletedCases} setDeletedCases={setDeletedCases} />;
      default: return (<NyRegistrering {...commonProps} editingCase={editingCase} clearEditingCase={clearEditingCase} hasUnsavedChanges={hasUnsavedChanges} setHasUnsavedChanges={setHasUnsavedChanges} originalCategory={originalCategory} />);
    }
  };

  // 1. Sjekk om vi holder på å sjekke autentisering
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Sjekker autentisering...</p>
        </div>
      </div>
    );
  }

  // 2. Hvis ikke autentisert - vis ALLTID login-skjermen først
  if (!isAuthenticated) {
    return (
      <>
        {/* Vis vedlikeholdsvarsel øverst hvis aktivt */}
        {maintenanceManager.isMaintenanceModeActive && (
          <div className="fixed top-0 left-0 right-0 bg-orange-500 text-white p-4 text-center z-50">
            <div className="flex items-center justify-center space-x-2">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-semibold">
                Vedlikeholdsmodus er aktiv - Kun administratorer kan logge inn
              </span>
            </div>
            <p className="text-sm mt-1 opacity-90">
              {maintenanceManager.maintenanceStatus?.message || 'Systemet er under vedlikehold'}
            </p>
          </div>
        )}
        <LoginScreen onLogin={handleLogin} />
      </>
    );
  }

  // 3. Hvis vi laster data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Starter systemet...</h2>
          <p className="text-gray-600">Kobler til Supabase Realtime</p>
        </div>
      </div>
    );
  }

  // 4. NÅ sjekker vi vedlikeholdsmodus (KUN for innloggede brukere)
 if (maintenanceManager.isMaintenanceModeActive) {
  const userName = localStorage.getItem('hagia-user-name');
  
  // Sjekk om bruker har vedlikeholdstilgang
  const hasMaintenanceAccess = userRole?.role === 'admin' || 
                               userRole?.permissions?.can_access_maintenance;
  
  if (!hasMaintenanceAccess) {
    return (
      <MaintenancePage 
        maintenanceStatus={maintenanceManager.maintenanceStatus}
        onLogout={() => {
          localStorage.removeItem("hagia-user-name");
          localStorage.removeItem("hagia-user-pin");
          localStorage.removeItem("hagia-login-time");
          localStorage.removeItem("hagia-last-activity");
          setIsAuthenticated(false);
          setUserRole(null);
        }}
      />
    );
  }
}

  // 5. Normal app render
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Maintenance Warning */}
      <MaintenanceWarning
        isVisible={showMaintenanceWarning}
        message={maintenanceWarningData?.message || ''}
        minutesUntil={maintenanceWarningData?.minutesUntil || 5}
        onDismiss={() => setShowMaintenanceWarning(false)}
      />
      
      {maintenanceManager.isMaintenanceModeActive && canAccessMaintenance && (
        <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-between">
          <span className="font-semibold">
            ⚠️ VEDLIKEHOLDSMODUS: Systemet er i vedlikeholdsmodus
          </span>
          <button
            onClick={async () => {
              if (confirm('Er du sikker på at du vil avslutte vedlikeholdsmodus?')) {
                const success = await supabaseService.setMaintenanceMode(false);
                if (success) {
                  window.location.reload();
                }
              }
            }}
            className="px-3 py-1 bg-white text-red-600 rounded text-sm font-medium hover:bg-gray-100 transition-colors"
          >
            Avslutt vedlikehold
          </button>
        </div>
      )}

      {/* Header */}
      <div
        className={`shadow-lg transition-all duration-300 ${
          currentApp === 'hagia'
            ? 'bg-gradient-to-r from-green-400 to-green-600'
            : 'bg-gradient-to-r from-blue-500 to-blue-700'
        }`}
        style={{ marginTop: showMaintenanceWarning ? '80px' : '0' }}
      >
            <div className="max-w-7xl mx-auto px-4 py-4">
              <div className="flex items-center justify-between">
                {/* VENSTRE: Logo + App Switcher + FeedbackBox */}
                <div className="flex items-center space-x-4 flex-shrink-0">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center shadow-md">
                    <span className="text-white font-bold text-xl">
                      {currentApp === 'hagia' ? 'H' : 'S'}
                    </span>
                  </div>
                  <AppSwitcher currentApp={currentApp} onSwitch={handleAppSwitch} />
                  <FeedbackBox />
                </div>

                {/* MIDT: Kjempestor søkeboks (kun for Hagia) */}
                {currentApp === 'hagia' && (
                  <div className="flex-1 mx-6" ref={searchRef}>
                  <div className="relative max-w-4xl mx-auto">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Search className="h-6 w-6 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Søk i alle ordre - kunde, maskin, ordre#, telefon, beskrivelse, serienummer..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full pl-12 pr-6 py-4 border-2 border-white border-opacity-50 rounded-2xl bg-white text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-50 transition-all text-base shadow-lg"
                    />
                    
                    {showSearchResults && searchResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto z-50">
                        <div className="p-2 bg-gray-50 border-b text-xs text-gray-600 font-medium">
                          {searchResults.length} resultat{searchResults.length !== 1 ? 'er' : ''} funnet
                        </div>
                        {searchResults.map((case_) => (
                          <div key={case_.id} onClick={() => handleSearchResultClick(case_)} 
                              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="font-semibold text-gray-900 text-sm">#{case_.id}</span>
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    case_.kategori === "Service" ? "bg-green-100 text-green-800" :
                                    case_.kategori === "Arkiv" ? "bg-gray-100 text-gray-800" :
                                    case_.kategori === "Bestilt" ? "bg-blue-100 text-blue-800" :
                                    case_.kategori === "Klar til henting" ? "bg-orange-100 text-orange-800" :
                                    case_.kategori === "Klar til fakturering" ? "bg-purple-100 text-purple-800" :
                                    "bg-gray-100 text-gray-800"
                                  }`}>
                                    {case_.kategori}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-900 font-medium">{case_.kunde}</div>
                                <div className="text-xs text-gray-600">{case_.maskinMerke} - {case_.beskrivelse}</div>
                                {case_.tlf && (
                                  <div className="text-xs text-gray-500 mt-1">📞 {case_.tlf}</div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-gray-500">
                                  {new Date(case_.dato).toLocaleDateString('nb-NO')}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* HØYRE: Brukerinfo og meny */}
                <div className="flex items-center space-x-4 text-white">
                  <div className="flex items-center space-x-2">
                    <User size={16} />
                    <span className="font-medium">{localStorage.getItem('hagia-user-name') || 'Ukjent'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Lock size={16} />
                    <span className="text-sm">
                      Innlogget {localStorage.getItem('hagia-login-time') 
                        ? new Date(localStorage.getItem('hagia-login-time')!).toLocaleTimeString('nb-NO', {hour: '2-digit', minute: '2-digit'})
                        : ''}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1 text-sm">
                    {isOnline && realtimeConnected ? (
                      <span className="text-green-300">🟢 Live</span>
                    ) : isOnline && !realtimeConnected ? (
                      <span className="text-yellow-300">🟡 Kobler til...</span>
                    ) : (
                      <>
                        <CloudOff size={16} />
                        <span>Offline</span>
                      </>
                    )}
                  </div>
                  <div className="relative" ref={dropdownRef}>
                    <button onClick={() => setShowDropdown(!showDropdown)} className="flex items-center space-x-1 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-lg transition-colors">
                      <Menu size={20} />
                      <ChevronDown size={16} className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showDropdown && (
                      <div 
                        ref={dropdownRef}
                        className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 z-50"
                      >
                        {/* Kunder - alltid synlig */}
                        <button
                          onClick={() => {
                            setActiveTab("kunder");
                            setShowDropdown(false);
                          }}
                          className="w-full flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                        >
                          <Users size={16} />
                          <span>Kunder</span>
                        </button>

                        {/* Produkter - alltid synlig */}
                        <button
                          onClick={() => {
                            setActiveTab("produkter");
                            setShowDropdown(false);
                          }}
                          className="w-full flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                        >
                          <Package size={16} />
                          <span>Produkter</span>
                        </button>

                        {/* Datasikring - kun for admin eller med tilgang */}
                        {(userRole?.role === 'admin' || userRole?.permissions?.can_access_reports) && (
                          <>
                            <div className="border-t my-2"></div>
                            <button
                              onClick={() => {
                                setActiveTab("datasikring");
                                setShowDropdown(false);
                              }}
                              className="w-full flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                            >
                              <Database size={16} />
                              <span>Datasikring</span>
                            </button>
                          </>
                        )}

                        {/* Admin-seksjonen - kun hvis bruker har admin-tilgang */}
                        {(canAccessMaintenance || userRole?.role === 'admin') && (
                          <>
                            <div className="border-t my-2"></div>
                            
                            {/* Vedlikehold - kun for de med tilgang */}
                            {canAccessMaintenance && (
                              <button
                                onClick={() => {
                                  setShowMaintenanceDialog(true);
                                  setShowDropdown(false);
                                }}
                                className="w-full flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                              >
                                <Settings size={16} />
                                <span>Vedlikehold</span>
                              </button>
                            )}

                            {/* Brukeradministrasjon - kun for admin */}
                            {userRole?.role === 'admin' && (
                              <button
                                onClick={() => {
                                  setShowUserManagement(true);
                                  setShowDropdown(false);
                                }}
                                className="w-full flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                              >
                                <Shield size={16} />
                                <span>Brukeradministrasjon</span>
                              </button>
                            )}
                          </>
                        )}

                        {/* Logg ut - alltid nederst */}
                        <div className="border-t my-2"></div>
                        <button
                          onClick={() => {
                            handleLogout();
                            setShowDropdown(false);
                          }}
                          className="w-full flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50"
                        >
                          <LogOut size={16} />
                          <span>Logg ut</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs (kun for Hagia) */}
          {currentApp === 'hagia' && (
            <div className="bg-white border-b shadow-sm">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex space-x-2 py-3 overflow-x-auto">
                {categories.map((category) => {
                  const Icon = category.icon;
                    const caseCount = category.id === "Arkiv" 
                      ? archivedCases.length 
                      : cases.filter((c) => c.kategori === category.id).length;
                  const isActive = activeTab === category.id;
                  
                  let tabColorClass = "";
                  if (category.id === "Til bestilling") {
                    const tilBestillingCases = cases.filter(c => c.kategori === "Til bestilling");
                    if (tilBestillingCases.length > 0) {
                      const now = new Date();
                      const hasOldCases = tilBestillingCases.some((case_) => {
                        const caseDate = new Date(case_.opprettet);
                        const diffInHours = (now.getTime() - caseDate.getTime()) / (1000 * 60 * 60);
                        return diffInHours > 72;
                      });
                      const hasMediumOldCases = tilBestillingCases.some((case_) => {
                        const caseDate = new Date(case_.opprettet);
                        const diffInHours = (now.getTime() - caseDate.getTime()) / (1000 * 60 * 60);
                        return diffInHours > 48;
                      });
                      if (hasOldCases) tabColorClass = "bg-red-100 text-red-700 border-red-300";
                      else if (hasMediumOldCases) tabColorClass = "bg-yellow-100 text-yellow-700 border-yellow-300";
                    }
                  }
                  
                  return (
                    <button 
                      key={category.id} 
                      onClick={() => handleTabChange(category.id)} 
                      className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap border ${
                        isActive 
                          ? "bg-gradient-to-r from-green-500 to-green-600 text-white border-green-600 shadow-md transform -translate-y-0.5" 
                          : tabColorClass || "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:shadow-sm"
                      }`}
                    >
                      <Icon size={16} />
                      <span>{category.name}</span>
                      {category.id !== "ny-registrering" && caseCount > 0 && (
                        <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${
                          isActive 
                            ? "bg-white bg-opacity-25 text-white" 
                            : "bg-gradient-to-r from-green-500 to-green-600 text-white"
                        }`}>
                          {caseCount}
                          {editingCase && cases.some(c => c.id === editingCase.id && c.kategori === category.id) && (
                            <span className="ml-1 text-yellow-300">✏️</span>
                          )}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          )}

          {/* Main Content */}
          <div className={currentApp === 'strand' ? '' : 'p-6'}>
            <div className={currentApp === 'strand' ? '' : 'max-w-7xl mx-auto'}>
              {currentApp === 'hagia' ? (
                renderActiveComponent()
              ) : (
                <StrandApp customers={customers} setCustomers={setCustomers} />
              )}
            </div>
          </div>

          {/* Dialogs */}
          {showUnsavedDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="text-yellow-600" size={20} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Ulagrede endringer</h3>
                </div>
                <p className="text-gray-600 mb-6">Du har ulagrede endringer som vil gå tapt hvis du forlater siden. Hva ønsker du å gjøre?</p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowUnsavedDialog(false);
                      setPendingTab(null);
                      setPendingApp(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Fortsett redigering
                  </button>
                  <button
                    onClick={() => {
                      clearEditingCase();
                      setHasUnsavedChanges(false);

                      // Håndter tab-bytte
                      if (pendingTab === "exit-editing") {
                        // Bare lukk redigeringsmodus
                      } else if (pendingTab) {
                        setActiveTab(pendingTab);
                      }

                      // Håndter app-bytte
                      if (pendingApp) {
                        setCurrentApp(pendingApp);
                      }

                      setShowUnsavedDialog(false);
                      setPendingTab(null);
                      setPendingApp(null);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Forkast endringer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Move Backwards Dialog */}
          {showMoveBackwardsDialog && moveBackwardsData && (
            <MoveBackwardsDialog
              isOpen={showMoveBackwardsDialog}
              onClose={handleMoveBackwardsCancel}
              onConfirm={handleMoveBackwardsConfirm}
              caseName={moveBackwardsData.caseName}
              fromCategory={moveBackwardsData.fromCategory}
              toCategory={moveBackwardsData.toCategory}
            />
          )}

          {/* Session Manager Dialog */}
          <InactivityWarningDialog
            showInactivityWarning={sessionManager.showInactivityWarning && isAuthenticated}
            warningCountdown={sessionManager.warningCountdown}
            extendSession={sessionManager.extendSession}
            handleInactivityLogout={sessionManager.handleInactivityLogout}
          />
          
          {/* Maintenance Dialog */}
          <MaintenanceDialog
            isOpen={showMaintenanceDialog}
            onClose={() => setShowMaintenanceDialog(false)}
            onMaintenanceStart={() => {
              maintenanceManager.checkMaintenanceStatus();
            }}
            isMaintenanceActive={maintenanceManager.isMaintenanceModeActive}
            maintenanceStatus={maintenanceManager.maintenanceStatus}
          />

          {/* Notifications */}
          <ToastContainer notifications={notifications} onClose={removeNotification} />

          <UserManagement
            isOpen={showUserManagement}
            onClose={() => setShowUserManagement(false)}
            currentUser={localStorage.getItem('hagia-user-name') || ''}
          />
        </div>
      );
};

export default App;